import type { MatchSummary, Side } from '@foosball/shared';
import { formatDistanceToNow } from 'date-fns';

import { Card } from './ui';

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  confirmed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  disputed: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  void: 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
} as const;

function names(match: MatchSummary, side: Side): string {
  return (
    match.players
      .filter((p) => p.side === side)
      .map((p) => p.displayName)
      .join(' & ') || '—'
  );
}

function delta(match: MatchSummary, side: Side): number | null {
  const player = match.players.find((p) => p.side === side);
  if (!player || player.ratingBefore === null || player.ratingAfter === null) return null;
  return player.ratingAfter - player.ratingBefore;
}

export function MatchCard({
  match,
  children,
}: {
  match: MatchSummary;
  children?: React.ReactNode;
}) {
  const deltaA = delta(match, 'A');
  const deltaB = delta(match, 'B');

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {match.format} · {formatDistanceToNow(new Date(match.playedAt), { addSuffix: true })}
        </span>
        <span
          className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[match.status]}`}
        >
          {match.status}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div className={match.winnerSide === 'A' ? 'font-semibold' : 'text-slate-500'}>
          {names(match, 'A')}
          {deltaA !== null && <RatingDelta value={deltaA} />}
        </div>

        <div className="text-center text-2xl font-bold tabular-nums">
          {match.scoreA}
          <span className="mx-1 text-slate-300">–</span>
          {match.scoreB}
        </div>

        <div className={`text-right ${match.winnerSide === 'B' ? 'font-semibold' : 'text-slate-500'}`}>
          {names(match, 'B')}
          {deltaB !== null && <RatingDelta value={deltaB} align="right" />}
        </div>
      </div>

      {match.disputeReason && (
        <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-950 dark:text-red-300">
          Disputed: {match.disputeReason}
        </p>
      )}

      {children && <div className="mt-4">{children}</div>}
    </Card>
  );
}

function RatingDelta({ value, align = 'left' }: { value: number; align?: 'left' | 'right' }) {
  return (
    <span
      className={`mt-0.5 block text-xs tabular-nums ${align === 'right' ? 'text-right' : ''} ${
        value >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
      }`}
    >
      {value >= 0 ? '+' : ''}
      {value}
    </span>
  );
}
